import { DropdownList, NumericInput, OmegaComponent, dates, events } from "@omegagrid/core";
import { DynamicPeriodBoundry, ParsedPeriod, parseIntervalPeriod, PeriodParsingOptions } from "../../model/period";
import { html, css } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { msg } from "@omegagrid/localize";

const PERIOD_TYPES: Record<DynamicPeriodBoundry | 'C', string> = {
	'D': 'D',
	'W': 'W',
	'M': 'M',
	'Q': 'Q',
	'Y': 'Y',
	'C': 'C',
};

@customElement('rp-filters-dynamicperiod')
export class DynamicPeriod extends OmegaComponent {

	static styles = [OmegaComponent.styles, css`
		.row1, .row2, .row3 {
			padding: 2px;
		}

		.row1, .row3 {
			display: flex;
			font-size: 9px;
			line-height: 9px;
			align-items: center;
			justify-content: space-between;
		}

		.row1 > * {
			flex: 1;
		}

		.row2 {
			display: flex;
			align-items: center;
			gap: 2px;
			height: 22px;

		}

		.row2 > * {
			height: 100%;
		}

		og-dropdown-list {
			flex: 0 0 21px;
			text-align: center;
		}

		og-numericinput {
			flex: 1;
			text-align: center;
		}

		og-calendar-dropdown {
			flex-grow: 1;
			flex-shrink: 3;
			text-align: center;
		}
	`];

	@property({type: Array})
	boundries: (DynamicPeriodBoundry | 'C')[] = ['D', 'M', 'Q', 'Y', 'C'];

	@property({type: String})
	value: string;

	@property({type: Object})
	periodParsingOptions: PeriodParsingOptions;

	@query('og-dropdown-list.boundry1')
	boundry1: DropdownList;

	@query('og-numericinput.offset1')
	offset1: NumericInput;

	@query('og-dropdown-list.granularity')
	granularity: DropdownList;

	@query('og-numericinput.offset2')
	offset2: NumericInput;

	@query('og-dropdown-list.boundry2')
	boundry2: DropdownList;

	@state()
	parsedPeriod: ParsedPeriod;

	willUpdate(props: Map<string, unknown>) {
		if ((props.has('value') || props.has('periodParsingOptions')) && this.value) {
			this.parsedPeriod = this.value ? parseIntervalPeriod(this.value, this.periodParsingOptions) : null;
		}
	}

	constructValue() {
		const parts = [];
		if (!this.boundry1.value) return null;

		if (this.boundry1.value == 'C') {
			parts.push('20200101');
		} else {
			parts.push(this.boundry1.value);
			if (this.offset1?.value) {
				parts.push(`${this.offset1.value > 0 ? '+' : '-'}${Math.abs(this.offset1.value).toString()}`);
			}
		}

		if (this.boundry2.value) parts.push(':');

		if (this.boundry2.value) {
			if (this.boundry2.value == 'C') {
				parts.push('20201231');
			} else {
				parts.push(this.boundry2.value);
				if (this.offset2?.value) {
					parts.push(`${this.offset2.value > 0 ? '+' : '-'}${Math.abs(this.offset2.value).toString()}`);
				}
			}
		} else if (this.granularity.value) {
			parts.push(':', ...parts);
		}

		if (this.granularity.value) parts.push(':', this.granularity.value);

		return parts.join('');
	}

	_onChange = () => {
		this.value = this.constructValue() || null;
		this.dispatchEvent(new events.ChangeEvent({value: this.value}));
	}

	render = () => html`
		<div class="row1">
			<div style="text-align: left; flex: 0">${msg('Min')}</div>
			<div style="text-align: center; flex: 0">${msg('Granularity')}</div>
			<div style="text-align: right; flex: 0">${msg('Max')}</div>
		</div>

		<div class="row2">
			<og-dropdown-list
				noCaret
				class="boundry1"
				.value="${this.parsedPeriod?.start?.type}"
				.items="${this.boundries.map(boundry => ({key: boundry, value: msg(PERIOD_TYPES[boundry])}))}"
				@change="${this._onChange}">
			</og-dropdown-list>

			${!this.parsedPeriod || this.parsedPeriod?.start?.type ? html`
				<og-numericinput
					buttons
					class="offset1"
					format="+#,0.#####"
					.value="${this.parsedPeriod?.start?.offset ?? ''}"
					@change="${this._onChange}">
				</og-numericinput>
			` : html`
				<og-calendar-dropdown
					noCaret
					.value="${this.parsedPeriod?.start?.date ?? new Date(2021, 1, 1)}"
					@change="${this._onChange}">
				</og-calendar-dropdown>
			`}

			<og-dropdown-list
				noCaret
				class="granularity"
				.value="${this.parsedPeriod?.granularity}"
				.items="${[
					{key: '', value: '-'},
					...this.boundries.filter(boundry => boundry !== 'C')
						.map(boundry => ({key: boundry, value: boundry}))
				]}"
				@change="${this._onChange}">
			</og-dropdown-list>

			${!this.parsedPeriod || this.parsedPeriod?.end?.type ? html`
				<og-numericinput
					buttons
					class="offset2"
					format="+#,0.#####"
					.value="${this.parsedPeriod?.end?.offset ?? ''}"
					@change="${this._onChange}">
				</og-numericinput>
			` : html`
				<og-calendar-dropdown
					noCaret
					.value="${this.parsedPeriod?.end?.date ?? new Date(2021, 1, 1)}"
					@change="${this._onChange}">
				</og-calendar-dropdown>
			`}

			<og-dropdown-list
				noCaret
				class="boundry2"
				.value="${this.parsedPeriod?.end?.type}"
				.items="${this.boundries.map(boundry => ({key: boundry, value: msg(PERIOD_TYPES[boundry])}))}"
				@change="${this._onChange}">
			</og-dropdown-list>
		</div>

		${this.parsedPeriod?.start?.date || this.parsedPeriod?.end?.date ? html`
			<div class="row3">
				${this.parsedPeriod?.start?.date ? html`
					<div style="text-align: center">
						${dates.format(this.parsedPeriod.start.date)}
					</div>
				` : ''}
				<div style="text-align: center; flex: 0">-</div>
				${this.parsedPeriod?.end?.date ? html`
					<div style="text-align: center">
						${dates.format(this.parsedPeriod.end.date)}
					</div>
				` : ''}
			</div>
		` : ''}
	`;

}
